import React, { useState, useRef, useEffect } from 'react'
import { getAuthToken, getAuthUserData } from '../../../utils/authManager'
import { Alert } from 'components'
import axios from 'axios'
import s from './styles.module.scss'

// BCN Ecomm base URL (Zomato–Blinkit style child app)
// Default to localhost:3002 in development, but can be overridden via env
const BCN_ECOMM_URL = process.env.REACT_APP_BCN_ECOMM_URL || 'http://localhost:3002'
// BCN Ecomm API base URL
const BCN_ECOMM_API_URL = process.env.REACT_APP_BCN_ECOMM_API_URL || 'http://localhost:3001/api'

export default function BCNEcommModal(props) {
  const [selectedRole, setSelectedRole] = useState(null)
  const [loading, setLoading] = useState(false)
  const [checkingAdmin, setCheckingAdmin] = useState(false)
  const [adminCheckInProgress, setAdminCheckInProgress] = useState(false)
  const [cachedAdminStatus, setCachedAdminStatus] = useState(null)
  const [existingUserType, setExistingUserType] = useState(null) // Store existing userType from BCNEcomm
  const [checkingUserType, setCheckingUserType] = useState(true)
  const [shakeAdmin, setShakeAdmin] = useState(false)
  const [isUserAdmin, setIsUserAdmin] = useState(null) // Store admin status
  const isProcessingRef = useRef(false)

  const userData = getAuthUserData()
  // Check for both user token (authorization) and admin token (authentication)
  const userToken = getAuthToken() // Gets 'authorization' key
  const adminToken = localStorage.getItem('authentication') // Admin token key
  const token = adminToken || userToken // Prefer admin token if exists

  // Check existing user type in BCNEcomm on mount
  useEffect(() => {
    const checkExistingUserType = async () => {
      // For admins, skip user type check (they don't have userRefId/phoneNo in same format)
      const adminData = localStorage.getItem('adminData') ? JSON.parse(localStorage.getItem('adminData')) : null
      if (adminData) {
        setCheckingUserType(false)
        setExistingUserType(null) // Admins are not sellers by default
        return
      }

      if (!token || !userData) {
        setCheckingUserType(false)
        return
      }

      try {
        // Check if user exists in BCNEcomm and what their userType is
        const checkUrl = new URL(`${BCN_ECOMM_API_URL}/auth/check-user-type`)
        if (userData.phoneNo) checkUrl.searchParams.append('phoneNo', userData.phoneNo)
        if (userData.userRefId) checkUrl.searchParams.append('userRefId', userData.userRefId)
        if (userData.email) checkUrl.searchParams.append('email', userData.email)

        const response = await axios.get(checkUrl.toString())
        
        if (response.data?.exists && response.data?.userType === 'seller') {
          setExistingUserType('seller')
          console.log('User is already a seller in BCNEcomm, hiding buyer option')
        } else {
          setExistingUserType(null)
        }
      } catch (error) {
        console.log('User may not exist in BCNEcomm yet or error checking:', error.message)
        // If endpoint doesn't exist or fails, assume user doesn't exist yet
        setExistingUserType(null)
      } finally {
        setCheckingUserType(false)
      }
    }

    checkExistingUserType()
  }, [token, userData])

  // Check if user is admin (for conditional admin card display)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (token && !checkingUserType) {
        try {
          const adminStatus = await isAdmin()
          console.log('Admin status check result:', adminStatus)
          setIsUserAdmin(adminStatus)
        } catch (error) {
          console.error('Error in admin status check useEffect:', error)
          setIsUserAdmin(false)
        }
      }
    }
    if (!checkingUserType) {
      checkAdminStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkingUserType, token])

  // Check if user is admin in BCNIndia
  const isAdmin = async () => {
    // Prevent multiple simultaneous requests
    if (adminCheckInProgress) {
      console.log('Admin check already in progress, waiting...')
      // Wait for existing check to complete
      while (adminCheckInProgress) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      return cachedAdminStatus || false
    }

    // Return cached result if available
    if (cachedAdminStatus !== null) {
      console.log('Using cached admin status:', cachedAdminStatus)
      return cachedAdminStatus
    }

    try {
      setCheckingAdmin(true)
      setAdminCheckInProgress(true)
      
      console.log('Checking admin status with token:', token ? token.substring(0, 20) + '...' : 'no token')
      
      // Use BCNEcomm backend as proxy to avoid CORS/host restrictions
      // BCNEcomm backend has Backendaccesskey to call BCNIndia API
      const response = await axios.get(`${BCN_ECOMM_API_URL}/auth/verify-admin-status`, {
        headers: {
          authorization: token,
          Authorization: token // Also try uppercase for compatibility
        }
      })
      
      console.log('Admin verification response:', {
        status: response.status,
        success: response.data?.success,
        isAdmin: response.data?.isAdmin,
        userType: response.data?.userType
      })
      
      // Check response structure - BCNEcomm proxy returns { success: true, isAdmin: true/false, ... }
      const isAdminResult = response.data?.success === true && 
                           response.data?.isAdmin === true
      
      // If admin, store admin userData (includes phoneNo) for later use
      if (isAdminResult && response.data?.userData) {
        // Store admin userData temporarily for use in role selection
        const adminUserData = response.data.userData
        if (!localStorage.getItem('adminData') || !JSON.parse(localStorage.getItem('adminData')).phoneNo) {
          // Update adminData with phoneNo from verification response
          const existingAdminData = localStorage.getItem('adminData') ? JSON.parse(localStorage.getItem('adminData')) : {}
          localStorage.setItem('adminData', JSON.stringify({
            ...existingAdminData,
            ...adminUserData,
            phoneNo: adminUserData.phoneNo,
            email: adminUserData.email || existingAdminData.email
          }))
        }
      }
      
      console.log('Admin check result:', isAdminResult)
      setCachedAdminStatus(isAdminResult)
      return isAdminResult
    } catch (error) {
      console.error('Error checking admin status:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })
      setCachedAdminStatus(false)
      return false
    } finally {
      setCheckingAdmin(false)
      setAdminCheckInProgress(false)
    }
  }

  const handleRoleSelection = async (role) => {
    // Prevent multiple simultaneous role selections
    if (isProcessingRef.current || loading) {
      console.log('Role selection already in progress, ignoring duplicate click')
      return
    }

    // For admins, check adminData; for users, check userData
    const currentAdminData = localStorage.getItem('adminData') ? JSON.parse(localStorage.getItem('adminData')) : null
    const hasAuth = token && (userData || currentAdminData)
    
    if (!token || !hasAuth) {
      Alert.error('Please login to access BCN Ecomm')
      props.onCloseModal()
      return
    }

    // Check if trying to access admin without being admin
    if (role === 'admin') {
      const adminStatus = await isAdmin()
      if (!adminStatus) {
        // Shake animation for disabled admin button
        setShakeAdmin(true)
        setTimeout(() => setShakeAdmin(false), 500)
        Alert.error('Access restricted. Only BCN India admins can access admin features.')
        return
      }
    }

    // Check if user is trying to access as buyer but is already a seller
    if (role === 'buyer' && existingUserType === 'seller') {
      Alert.error('You are already registered as a seller. Sellers cannot access as buyers. Please use "Seller Dashboard" option.')
      return
    }

    isProcessingRef.current = true
    setSelectedRole(role)
    setLoading(true)

    try {
      // Prepare authentication data
      // For admins, get phoneNo from adminData (which should have been updated from verification response)
      // For users, use userData
      const currentAdminData = localStorage.getItem('adminData') ? JSON.parse(localStorage.getItem('adminData')) : null
      const isAdminUser = !!adminToken || !!currentAdminData
      
      const phoneNo = isAdminUser ? (currentAdminData?.phoneNo || '') : (userData?.phoneNo || '')
      const userRefId = isAdminUser ? null : (userData?.userRefId || '')

      // Use secure token exchange instead of URL-based passing
      // This is more secure and works for both web and mobile apps
      try {
        const exchangeResponse = await axios.post(
          `${BCN_ECOMM_API_URL}/auth/exchange-token`,
          {
            bcnIndiaToken: token,
            phoneNo: phoneNo,
            userRefId: userRefId,
            userType: role
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )

        if (exchangeResponse.data && exchangeResponse.data.token) {
          // Token exchange successful - pass token via URL (more secure than passing BCN India token)
          // The exchanged token is shorter-lived and specific to BCN Ecomm
          const ecommToken = exchangeResponse.data.token
          
          // Create the BCN Ecomm URL with exchanged token
          // This is more secure than passing the original BCN India token
          const bcnEcommUrl = `${BCN_ECOMM_URL}/login?ecommToken=${encodeURIComponent(ecommToken)}&userType=${role}`
          
          // Open BCN Ecomm in a new tab (like Zomato opens Blinkit)
          window.open(bcnEcommUrl, '_blank')
          
          Alert.success(`Opening ${role === 'seller' ? 'Seller Dashboard' : role === 'admin' ? 'Admin Panel' : 'BCN Ecomm'}...`)
          props.onCloseModal()
        } else {
          throw new Error('Token exchange failed: No token received')
        }
      } catch (exchangeError) {
        console.error('Token exchange error:', exchangeError)
        
        // Fallback to URL-based method for backward compatibility
        // This ensures the feature still works if token exchange fails
        console.warn('Falling back to URL-based token passing')
        
        const authData = {
          phoneNo: phoneNo,
          userRefId: userRefId,
          authorization: token,
          userType: role,
          name: isAdminUser ? (currentAdminData?.name || '') : (userData?.name || ''),
          email: isAdminUser ? (currentAdminData?.email || '') : (userData?.email || '')
        }

        // Encode the data as base64 for URL transmission
        const encodedData = btoa(JSON.stringify(authData))
        
        // Create the BCN Ecomm URL with encoded auth data
        const bcnEcommUrl = `${BCN_ECOMM_URL}/login?bcnIndiaAuth=${encodedData}`
        
        // Open BCN Ecomm in a new tab
        window.open(bcnEcommUrl, '_blank')
        
        Alert.success(`Opening ${role === 'seller' ? 'Seller Dashboard' : role === 'admin' ? 'Admin Panel' : 'BCN Ecomm'}...`)
        props.onCloseModal()
      }
    } catch (error) {
      console.error('Error navigating to BCN Ecomm:', error)
      
      // Check if error is about role downgrade
      if (error.response?.data?.existingUserType === 'seller' && error.response?.data?.requestedUserType === 'buyer') {
        Alert.error('You are already registered as a seller. Sellers cannot access as buyers.')
      } else {
        Alert.error('Failed to open BCN Ecomm. Please try again.')
      }
      
      setLoading(false)
      isProcessingRef.current = false
      setSelectedRole(null)
    }
  }

  // For admins, check adminData; for users, check userData
  const currentAdminData = localStorage.getItem('adminData') ? JSON.parse(localStorage.getItem('adminData')) : null
  const hasAuth = token && (userData || currentAdminData)

  if (!token || !hasAuth) {
    return (
      <div className={s.main}>
        <div className={s.confirm}>
          <div className={s.confirmInner}>
            <div className={s.title}>Please login to access BCN Ecomm</div>
            <div className={s.actionIcons}>
              <div className={s.button} onClick={props.onCloseModal}>
                <span className='material-icons-outlined'>arrow_back</span> Close
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Determine if buyer option should be shown
  // Hide buyer option if user is already a seller in BCNEcomm
  const showBuyerOption = existingUserType !== 'seller'

  if (checkingUserType) {
    return (
      <div className={s.main}>
        <div className={s.confirm}>
          <div className={s.confirmInner}>
            <div className={s.title}>Welcome to BCN E-Commerce</div>
            <div className={s.loadingContainer}>
              <div className={s.loadingSpinnerLarge}></div>
              <div className={s.loadingText}>Checking your account status...</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={s.main}>
      <div className={s.confirm}>
        <div className={s.confirmInner}>
          <div className={s.title}>Welcome to BCN E-Commerce</div>
          <div className={s.subtitle}>Access your available dashboard</div>
            
          {existingUserType === 'seller' && (
            <div className={s.roleStatusCard}>
              <span className={`material-icons-outlined ${s.statusIcon}`}>info</span>
              <div className={s.statusContent}>
                <div className={s.statusTitle}>Your Account Role: Seller</div>
                <div className={s.statusDescription}>You can access seller features such as product management and order handling.</div>
              </div>
            </div>
          )}
            
          <div className={s.roleButtons}>
            {/* Buyer Dashboard - Show for new users or existing buyers */}
            {showBuyerOption && (
              <button
                className={`${s.roleButton} ${s.buyerButton} ${s.primary}`}
                onClick={() => handleRoleSelection('buyer')}
                disabled={loading && selectedRole !== 'buyer'}
              >
                {loading && selectedRole === 'buyer' ? (
                  <div className={s.loadingSpinner}></div>
                ) : (
                  <span className={`material-icons-outlined ${s.icon}`}>shopping_cart</span>
                )}
                <div className={s.buttonText}>
                  <span className={s.buttonLabel}>
                    {loading && selectedRole === 'buyer' ? 'Opening...' : 'Shop as Buyer'}
                  </span>
                  <span className={s.buttonDescription}>Browse and purchase products</span>
                </div>
                {!loading || selectedRole !== 'buyer' ? (
                  <span className={s.arrow}>→</span>
                ) : null}
              </button>
            )}

            {/* Seller Dashboard - Show for all users (but primary only if not buyer) */}
            <button
              className={`${s.roleButton} ${s.sellerButton} ${showBuyerOption ? s.secondary : s.primary}`}
              onClick={() => handleRoleSelection('seller')}
              disabled={loading && selectedRole !== 'seller'}
            >
              {loading && selectedRole === 'seller' ? (
                <div className={s.loadingSpinner}></div>
              ) : (
                <span className={`material-icons-outlined ${s.icon}`}>store</span>
              )}
              <div className={s.buttonText}>
                <span className={s.buttonLabel}>
                  {loading && selectedRole === 'seller' ? 'Opening...' : 'Seller Dashboard'}
                </span>
                <span className={s.buttonDescription}>Manage products, inventory & orders</span>
              </div>
              {!loading || selectedRole !== 'seller' ? (
                <span className={s.arrow}>→</span>
              ) : null}
            </button>

            {/* Secondary: Admin Panel - Conditional Access */}
            <button
              className={`${s.roleButton} ${s.adminButton} ${s.secondary} ${(isUserAdmin === false) ? s.locked : ''} ${shakeAdmin ? s.shake : ''}`}
              onClick={() => handleRoleSelection('admin')}
              disabled={(loading && selectedRole !== 'admin') || checkingAdmin || (isUserAdmin === false)}
            >
              {checkingAdmin || (loading && selectedRole === 'admin') ? (
                <div className={s.loadingSpinner}></div>
              ) : (
                <span className={`material-icons-outlined ${s.icon}`}>admin_panel_settings</span>
              )}
              <div className={s.buttonText}>
                <span className={s.buttonLabel}>
                  {checkingAdmin ? 'Checking...' : loading && selectedRole === 'admin' ? 'Opening...' : 'Admin Panel'}
                </span>
                <span className={s.buttonDescription}>
                  {isUserAdmin === false ? '(Access restricted)' : isUserAdmin === true ? 'Platform & user management' : 'Checking access...'}
                </span>
              </div>
              {!checkingAdmin && (!loading || selectedRole !== 'admin') && isUserAdmin === true ? (
                <span className={s.arrow}>→</span>
              ) : null}
            </button>
          </div>
          
          <div className={s.actionIcons}>
            <div className={s.button} onClick={props.onCloseModal}>
              <span className='material-icons-outlined'>arrow_back</span> Back to BCN India
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
